import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiTrash2, FiDownload, FiFile, FiEdit2, FiPrinter } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDialog, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { formatMoney, formatDate, labelify } from '../../utils/format';
import { voucherToPDF } from '../../utils/export';
import { COMPANY_NAME } from '../../config';

export default function VoucherDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, sRes] = await Promise.all([
        api.vouchers.get(id),
        api.settings.get(),
      ]);
      setV(vRes.data.voucher || vRes.data);
      const raw = sRes.data.settings || sRes.data || {};
      const flat = {};
      if (typeof raw === 'object' && !Array.isArray(raw)) {
        Object.values(raw).forEach((group) => {
          if (Array.isArray(group)) {
            group.forEach((s) => { flat[s.key] = s.value; });
          }
        });
      }
      setSettings(flat);
    } catch (err) {
      showError('Failed to load voucher', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (fn, msg) => {
    const res = await confirmDialog({ title: `Confirm ${msg}?`, confirmText: `Yes, ${msg.toLowerCase()}` });
    if (!res.isConfirmed) return;
    try {
      await fn();
      toast('success', '', `Voucher ${msg.toLowerCase()} successfully`);
      load();
    } catch (err) {
      showError(`Failed to ${msg.toLowerCase()} voucher`, extractError(err));
    }
  };

  const handleDelete = async () => {
    const res = await confirmDelete('this voucher');
    if (!res.isConfirmed) return;
    try {
      await api.vouchers.remove(id);
      toast('success', '', 'Voucher deleted');
      navigate('/vouchers');
    } catch (err) {
      showError('Failed to delete voucher', extractError(err));
    }
  };

  const handlePDF = async (print = false) => {
    try {
      await voucherToPDF(v, COMPANY_NAME, settings, print);
      toast('success', '', print ? 'Print dialog opened' : 'PDF downloaded');
    } catch (err) {
      showError('Failed to generate PDF', extractError(err));
    }
  };

  const downloadAttachment = async (index) => {
    const att = (v.attachments || [])[index];
    if (!att) return;
    try {
      const res = await api.vouchers.attachment(id, index);
      const blob = new Blob([res.data], { type: att.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name || `attachment-${index + 1}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError('Failed to download attachment', extractError(err));
    }
  };

  if (loading) return <Loader />;
  if (!v) return null;

  const entries = v.entries || [];
  const totalDebit = entries.reduce((a, e) => a + (Number(e.debit) || 0), 0);
  const totalCredit = entries.reduce((a, e) => a + (Number(e.credit) || 0), 0);
  const isPosted = v.status === 'posted';
  const isVoid = v.status === 'void';

  return (
    <div className="page">
      <PageHeader
        title={v.voucher_no}
        subtitle="Voucher details and accounting entries."
        crumbs={[{ label: 'Finance', to: '/vouchers' }, { label: 'Vouchers', to: '/vouchers' }, { label: v.voucher_no }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/vouchers')}><FiArrowLeft /> Back</button>
            <button className="btn btn-secondary" onClick={() => handlePDF()}><FiDownload /> Download PDF</button>
            <button className="btn btn-secondary" onClick={() => handlePDF(true)}><FiPrinter /> Print</button>
            {hasPermission('create-vouchers') && !isVoid && (
              <button className="btn btn-primary" onClick={() => navigate(`/vouchers/${id}/edit`)}><FiEdit2 /> Edit</button>
            )}
            {hasPermission('delete-vouchers') && (
              <button className="btn btn-danger-outline" onClick={handleDelete}><FiTrash2 /> Delete</button>
            )}
            {!isPosted && !isVoid && (
              <button className="btn btn-success" onClick={() => action(() => api.vouchers.post(id), 'Post')}>
                <FiCheckCircle /> Post
              </button>
            )}
            {!isPosted && !isVoid && (
              <button className="btn btn-warning" onClick={() => action(() => api.vouchers.void(id), 'Void')}>
                <FiXCircle /> Void
              </button>
            )}
          </>
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="detail-grid">
            <div className="detail-item"><div className="d-label">Voucher No</div><div className="d-value">{v.voucher_no}</div></div>
            <div className="detail-item"><div className="d-label">Type</div><div className="d-value">{labelify(v.voucher_type)}</div></div>
            <div className="detail-item"><div className="d-label">Date</div><div className="d-value">{formatDate(v.date)}</div></div>
            <div className="detail-item"><div className="d-label">Status</div><div className="d-value"><StatusBadge status={v.status} /></div></div>
            <div className="detail-item" style={{ gridColumn: '1 / -1' }}><div className="d-label">Narration</div><div className="d-value" style={{ textTransform: 'uppercase' }}>{v.narration || '-'}</div></div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Entries</div></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Code</th>
                <th className="table-num">Debit</th>
                <th className="table-num">Credit</th>
                <th>Narration</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>{e.account ? e.account.name : '-'}</td>
                   <td>{e.account ? e.account.name : '-'}</td>
                  <td className="table-num amount-debit">{Number(e.debit) ? formatMoney(e.debit) : ''}</td>
                  <td className="table-num amount-credit">{Number(e.credit) ? formatMoney(e.credit) : ''}</td>
                  <td style={{ textTransform: 'uppercase' }}>{e.narration || '-'}</td>
                </tr>
              ))}
              <tr style={{ background: '#f8fafc' }}>
                <td colSpan={2} style={{ fontWeight: 700 }}>Totals</td>
                <td className="table-num amount-debit">{formatMoney(totalDebit)}</td>
                <td className="table-num amount-credit">{formatMoney(totalCredit)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {v.attachments && v.attachments.length > 0 && (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-header"><div className="card-title">Attachments ({v.attachments.length})</div></div>
          <div className="card-body">
            <div className="attachment-list">
              {v.attachments.map((att, i) => {
                const isImage = att.mime_type && att.mime_type.startsWith('image/');
                return (
                  <div key={i} className="attachment-item">
                    {isImage
                      ? <img className="attachment-thumb" src={att.data_base64} alt={att.file_name} />
                      : <span className="attachment-file"><FiFile /></span>}
                    <div className="attachment-info">
                      <div className="attachment-name">{att.file_name}</div>
                      <div className="attachment-meta">
                        {att.mime_type || 'file'}
                        {att.size ? ` · ${(att.size / 1024).toFixed(1)} KB` : ''}
                      </div>
                    </div>
                    {isImage && (
                      <a className="btn-icon" title="Open preview" href={att.data_base64} target="_blank" rel="noreferrer">
                        <FiDownload />
                      </a>
                    )}
                    <button className="btn-icon" title="Download" onClick={() => downloadAttachment(i)}>
                      <FiDownload />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}